import { isEmpty } from 'lodash';

import type { AgentResponse, AgentUpdateRequest } from '../api';
import {
  parseGreetingsObjectString,
  formatGreetingsObjectsToString,
} from '../components/AgentForm/utils/greetingsUtils';
import { toApiBackgroundSound, toFormBackgroundSound } from './backgroundAudio';
import { buildTurnDetectorConfig } from '../components/AgentForm/utils/turnDetector';
import {
  sanitizePatternsForApi,
  type VoicemailDetectionPatterns,
} from '../components/AgentForm/utils/voicemailDetectionPatterns';
import type {
  TurnDetectorMode,
  TurnDetectorConfig,
  EndpointingMode,
  InterruptionMode,
  RealtimeVadType,
  RealtimeVadEagerness,
  VadSampleRate,
} from '../components/AgentForm/types';
import {
  TURN_DETECTOR_MODE,
  ENDPOINTING_MODE,
  REALTIME_VAD_TYPE,
  REALTIME_VAD_EAGERNESS,
  VAD_SAMPLE_RATE,
} from '../components/AgentForm/const';

export type { TurnDetectorMode, TurnDetectorConfig };
export type SilenceTrigger = {
  sec: number;
  msg: string;
};
export type AgentFormSettingsType = {
  keywords?: string;
  activation_phrases?: string;
  who_speaks_first?: NonNullable<
    AgentUpdateRequest['settings']
  >['who_speaks_first'];
};
export type PipelineSettings = {
  max_llm_timeout?: number;
  use_agent_steps?: boolean;
  domains_whitelist?: string;
  empty_room_timeout?: number;
  request_cleaner_code?: string;
  is_record_audio_allowed?: boolean;
  language_switch_by_request?: boolean;
  supported_languages?: string[];

  check_violations?: string;
  check_agent_violations?: string;

  automute_timeout_sec?: number;
  autoend_after_mute_timeout_sec?: number;
  min_endpointing_delay?: number;

  interrupt_min_words?: number;
  interrupt_speech_duration?: number;

  rag_collection_id?: number;

  max_endpointing_delay?: number;

  room_life_time_limit?: number;

  clean_user_input_code_box?: string;
  code_box_debug_on?: boolean;
  overall_retry_interval?: number;
  max_overall_attempts?: number;
  overall_stt_segment_deadline?: number;
  internal_processing_allowance?: number;
  use_parallel_stt?: boolean;
  custom_tool_configurations?: string;
  ppx_model?: string;
  ppx_temperature?: number;
  ppx_timeout_sec?: number;
  ppx_search_recency_filter?: string | null;
  ppx_search_context_size?: string;
  ppx_enhanced_query?: string | null;
  ppx_search_domain_filter?: string[] | null;
  ppx_max_tokens?: number;
  n8n_whatsup_url?: string;
  vault_enabled?: boolean;
  log_level?: string;
  simulate_heygen_delay?: boolean;
  on_phone_call_started_webhook?: string;
  on_phone_call_ended_webhook?: string;
  conversation_metrics_prompt?: string;
  pii_masking_prompt?: string;
  agent_preset_greetings?: string;
  llm_widget_transcribe?: boolean;
  llm_widget_transcribe_prompt?: string | null;
  use_datetime_tool?: boolean;
  use_terminate_call_tool?: boolean;
  trigger_next_agent_response?: boolean;
  tts_remove_exact_matches?: string;
  tts_remove_markers?: string;
  relatedAgentIds?: string[];
  background_audio_enabled?: boolean;
  background_audio_ambient_sound?: string | null;
  background_audio_ambient_volume?: number;
  background_audio_ambient_custom_url?: string | null;
  background_audio_thinking_sound?: string | null;
  background_audio_thinking_volume?: number;
  background_audio_thinking_custom_url?: string | null;
  silence_triggers?: SilenceTrigger[];

  turn_detector_mode?: TurnDetectorMode;
  turn_detector_endpointing_mode?: EndpointingMode;
  turn_detector_endpointing_min_delay?: number;
  turn_detector_endpointing_max_delay?: number;
  turn_detector_interruption_mode?: InterruptionMode;
  turn_detector_interruption_false_interruption_timeout?: number;
  turn_detector_interruption_resume_false_interruption?: boolean;
  turn_detector_interruption_discard_audio_if_uninterruptible?: boolean;
  turn_detector_vad_activation_threshold?: number;
  turn_detector_vad_min_speech_duration?: number;
  turn_detector_vad_min_silence_duration?: number;
  turn_detector_vad_prefix_padding_duration?: number;
  turn_detector_vad_max_buffered_speech?: number;
  turn_detector_vad_sample_rate?: VadSampleRate;
  turn_detector_vad_force_cpu?: boolean;
  turn_detector_model_unlikely_threshold?: number | null;
  turn_detector_model_model_path?: string | null;
  turn_detector_realtime_vad_type?: RealtimeVadType;
  turn_detector_realtime_vad_eagerness?: RealtimeVadEagerness;
  turn_detector_realtime_vad_threshold?: number | null;
  turn_detector_realtime_vad_prefix_padding_ms?: number | null;
  turn_detector_realtime_vad_silence_duration_ms?: number | null;

  voicemail_detection_enabled?: boolean;
  voicemail_detection_max_listen_seconds?: number;
  voicemail_detection_max_words?: number;
  voicemail_detection_patterns?: VoicemailDetectionPatterns;
};

export type AgentForm = Omit<
  AgentUpdateRequest,
  'widget_settings' | 'settings' | 'pipeline_settings'
> &
  AgentFormSettingsType &
  PipelineSettings & {
    interrupt_min_words?: number;
  } & {
    id?: string;
    conversation_flow?: string;
    domains_white_list?: string;
    link_mic_audio?: boolean;
    preemptive_synthesis?: boolean;
    clean_user_input_code_box?: string;
    code_box_debug_on?: boolean;
    tts_fallback_settings?: string;
    rag_similarity_threshold?: number;
    rag_max_response_chunks?: number;
    use_perplexity?: boolean;
    skip_empty_user_messages?: boolean;
    room_lifetime_sec_with_no_users?: number;
    supported_languages?: string;
    on_agent_init_n8n_webhook?: string;
    overall_retry_interval?: number;
    max_overall_attempts?: number;
    overall_stt_segment_deadline?: number;
    internal_processing_allowance?: number;
    use_parallel_stt?: boolean;
    stt_fallback_settings?: string;
    is_rag_enabled?: boolean;
    parallel_stt_debug_mode?: boolean;
    ppx_search_domain_filter?: string[] | null;
    conversation_metrics_prompt?: string;
    pii_masking_prompt?: string;
    agent_preset_greetings?: string;
    tts_remove_exact_matches?: string;
    tts_remove_markers?: string;
  };
export type { AgentUpdateRequest };

const arrayToString = (array: Array<string>) => {
  return (array || []).join('\n');
};
export const stringToArray = (string: string) => {
  return (string || '').split('\n').filter(s => !!s.length);
};

export const stringToArrayQuote = (
  input: string,
  separator: string = ','
): string[] => {
  if (!input) return [];

  return input.split(separator).map(item => `${item.trim()}`);
};

export const arrayToCommaSeparatedString = (
  arr: string[],
  joiner: string = ','
): string => {
  return arr.join(joiner);
};

const convertKeywordsToString = (
  keywords: NonNullable<AgentUpdateRequest['settings']>['keywords']
) => {
  return (keywords || [])
    .map(([word, count]: any) => `${word}:${count}`)
    .join('\n');
};
const convertStringToKeywords = (string: string): any => {
  return (string || '')
    .split('\n')
    .filter(s => !!s.length)
    .map(string => {
      const [word, count] = string.split(':');
      if (!word || !count) return null;

      return [word, parseInt(count)];
    })
    .filter(w => !!w);
};

export const formatAgentToForm = (agentFromApi: AgentResponse): AgentForm => {
  const {
    settings,
    pipeline_settings,
    widget_settings,

    active,
    agent_name,
    agent_master_id,

    language,
    llm_primary_model_id,
    stt_primary_model_id,
    tts_primary_model_id,
    stt_primary_model_settings,
    stt_backup_model_settings,
    tts_primary_model_settings,
    tts_backup_model_settings,
    temperature,
    conversation_flow,

    ...rest
  } = agentFromApi;

  const {
    turn_detector: rawTurnDetector,
    voicemail_detection: rawVoicemailDetection,
    ...pipelineWithoutManagedNested
  } = pipeline_settings ?? {};

  const newTurnDetector: TurnDetectorConfig | undefined =
    rawTurnDetector && typeof rawTurnDetector === 'object'
      ? (rawTurnDetector as TurnDetectorConfig)
      : undefined;

  const ambientBackground = toFormBackgroundSound(
    pipeline_settings?.background_audio?.ambient_sound
  );
  const thinkingBackground = toFormBackgroundSound(
    pipeline_settings?.background_audio?.thinking_sound
  );

  return {
    ...rest,
    ...settings,
    ...pipelineWithoutManagedNested,
    temperature: temperature ?? 0.3,

    active: active ?? true,
    agent_name: agent_name || '',
    agent_master_id: agent_master_id || '',

    language: (language || 'en') as any,
    llm_primary_model_id: llm_primary_model_id || '',
    stt_primary_model_id: stt_primary_model_id || '',
    tts_primary_model_id: tts_primary_model_id || '',
    stt_primary_model_settings,
    stt_backup_model_settings,
    tts_primary_model_settings,
    tts_backup_model_settings,
    conversation_flow: !isEmpty(conversation_flow)
      ? JSON.stringify(conversation_flow)
      : '',
    max_llm_timeout: pipeline_settings?.max_llm_timeout ?? 3,

    keywords: convertKeywordsToString(settings?.keywords ?? []),
    activation_phrases: arrayToString(settings?.activation_phrases ?? []),
    check_violations: arrayToString(pipeline_settings?.check_violations ?? []),
    check_agent_violations: arrayToString(
      pipeline_settings?.check_agent_violations ?? []
    ),
    domains_white_list: arrayToCommaSeparatedString(
      pipeline_settings?.domains_white_list ?? []
    ),
    agent_preset_greetings: formatGreetingsObjectsToString(
      pipeline_settings?.agent_preset_greetings ?? []
    ),
    tts_fallback_settings: pipeline_settings?.tts_fallback_settings
      ? JSON.stringify(pipeline_settings?.tts_fallback_settings)
      : '',
    tts_remove_exact_matches: pipeline_settings?.tts_remove_exact_matches
      ? JSON.stringify(pipeline_settings?.tts_remove_exact_matches)
      : '',
    tts_remove_markers: pipeline_settings?.tts_remove_markers
      ? JSON.stringify(pipeline_settings?.tts_remove_markers)
      : '',
    stt_fallback_settings: pipeline_settings?.stt_fallback_settings
      ? JSON.stringify(pipeline_settings?.stt_fallback_settings)
      : '',
    link_mic_audio: pipeline_settings?.link_mic_audio ?? false,
    preemptive_synthesis: pipeline_settings?.preemptive_synthesis ?? true,
    clean_user_input_code_box: pipeline_settings?.clean_user_input_code_box,
    code_box_debug_on: pipeline_settings?.code_box_debug_on ?? false,
    rag_collection_id: pipeline_settings?.rag_collection_id,
    rag_similarity_threshold:
      pipeline_settings?.rag_similarity_threshold ?? 0.5,
    rag_max_response_chunks: pipeline_settings?.rag_max_response_chunks,
    use_perplexity: pipeline_settings?.use_perplexity ?? false,
    skip_empty_user_messages:
      pipeline_settings?.skip_empty_user_messages ?? false,
    room_lifetime_sec_with_no_users:
      pipeline_settings?.room_lifetime_sec_with_no_users,
    language_switch_by_request:
      pipeline_settings?.language_switch_by_request ?? false,
    supported_languages: pipeline_settings?.supported_languages,
    on_agent_init_n8n_webhook: pipeline_settings?.on_agent_init_n8n_webhook,
    overall_retry_interval: pipeline_settings?.overall_retry_interval,
    max_overall_attempts: pipeline_settings?.max_overall_attempts,
    overall_stt_segment_deadline:
      pipeline_settings?.overall_stt_segment_deadline,
    internal_processing_allowance:
      pipeline_settings?.internal_processing_allowance,
    use_parallel_stt: pipeline_settings?.use_parallel_stt,
    is_rag_enabled: pipeline_settings?.is_rag_enabled ?? false,
    custom_tool_configurations: pipeline_settings?.custom_tool_configurations
      ? JSON.stringify(pipeline_settings.custom_tool_configurations, null, 2)
      : '',
    parallel_stt_debug_mode: pipeline_settings?.parallel_stt_debug_mode,
    ppx_model: pipeline_settings?.ppx_model || 'sonar-pro',
    ppx_temperature: pipeline_settings?.ppx_temperature ?? 0.2,
    ppx_timeout_sec: pipeline_settings?.ppx_timeout_sec ?? 10,
    ppx_search_recency_filter:
      pipeline_settings?.ppx_search_recency_filter || null,
    ppx_search_context_size:
      pipeline_settings?.ppx_search_context_size ?? 'medium',
    ppx_enhanced_query: pipeline_settings?.ppx_enhanced_query || null,
    ppx_search_domain_filter:
      pipeline_settings?.ppx_search_domain_filter || null,
    ppx_max_tokens: pipeline_settings?.ppx_max_tokens ?? 200,
    n8n_whatsup_url: pipeline_settings?.n8n_whatsup_url || null,
    vault_enabled: pipeline_settings?.vault_enabled ?? false,
    log_level: pipeline_settings?.log_level ?? 'DEBUG',
    simulate_heygen_delay: pipeline_settings?.simulate_heygen_delay ?? false,
    on_phone_call_started_webhook:
      pipeline_settings?.on_phone_call_started_webhook || null,
    on_phone_call_ended_webhook:
      pipeline_settings?.on_phone_call_ended_webhook || null,
    conversation_metrics_prompt:
      pipeline_settings?.conversation_metrics_prompt || null,
    pii_masking_prompt: pipeline_settings?.pii_masking_prompt || null,
    llm_widget_transcribe: pipeline_settings?.llm_widget_transcribe ?? false,
    llm_widget_transcribe_prompt:
      pipeline_settings?.llm_widget_transcribe_prompt || null,
    use_datetime_tool: pipeline_settings?.use_datetime_tool ?? true,
    use_terminate_call_tool:
      pipeline_settings?.use_terminate_call_tool ?? false,
    trigger_next_agent_response:
      pipeline_settings?.trigger_next_agent_response ?? false,
    background_audio_enabled:
      pipeline_settings?.background_audio?.enabled ?? false,
    background_audio_ambient_sound: ambientBackground.sound,
    background_audio_ambient_custom_url: ambientBackground.customUrl,
    background_audio_ambient_volume:
      pipeline_settings?.background_audio?.ambient_volume ?? 0.3,
    background_audio_thinking_sound: thinkingBackground.sound,
    background_audio_thinking_custom_url: thinkingBackground.customUrl,
    background_audio_thinking_volume:
      pipeline_settings?.background_audio?.thinking_volume ?? 0.5,
    silence_triggers: pipeline_settings?.silence_triggers ?? [],

    voicemail_detection_enabled:
      rawVoicemailDetection?.enabled ?? false,
    voicemail_detection_max_listen_seconds:
      rawVoicemailDetection?.max_listen_seconds ?? 20,
    voicemail_detection_max_words:
      rawVoicemailDetection?.max_words ?? 30,
    voicemail_detection_patterns:
      (rawVoicemailDetection?.patterns as VoicemailDetectionPatterns | undefined) ?? {},

    turn_detector_mode:
      newTurnDetector?.mode ?? TURN_DETECTOR_MODE.MULTILINGUAL_MODEL,
    turn_detector_endpointing_mode:
      newTurnDetector?.endpointing?.mode ?? ENDPOINTING_MODE.FIXED,
    turn_detector_endpointing_min_delay:
      newTurnDetector?.endpointing?.min_delay,
    turn_detector_endpointing_max_delay:
      newTurnDetector?.endpointing?.max_delay,
    turn_detector_interruption_mode:
      newTurnDetector?.interruption?.mode ?? null,
    turn_detector_interruption_false_interruption_timeout:
      newTurnDetector?.interruption?.false_interruption_timeout ?? 1.0,
    turn_detector_interruption_resume_false_interruption:
      newTurnDetector?.interruption?.resume_false_interruption ?? true,
    turn_detector_interruption_discard_audio_if_uninterruptible:
      newTurnDetector?.interruption?.discard_audio_if_uninterruptible ?? true,
    turn_detector_vad_activation_threshold:
      newTurnDetector?.vad?.activation_threshold ?? 0.5,
    turn_detector_vad_min_speech_duration:
      newTurnDetector?.vad?.min_speech_duration ?? 0.05,
    turn_detector_vad_min_silence_duration:
      newTurnDetector?.vad?.min_silence_duration ?? 0.55,
    turn_detector_vad_prefix_padding_duration:
      newTurnDetector?.vad?.prefix_padding_duration ?? 0.5,
    turn_detector_vad_max_buffered_speech:
      newTurnDetector?.vad?.max_buffered_speech ?? 60.0,
    turn_detector_vad_sample_rate:
      newTurnDetector?.vad?.sample_rate ?? VAD_SAMPLE_RATE.RATE_16K,
    turn_detector_vad_force_cpu: newTurnDetector?.vad?.force_cpu ?? true,
    turn_detector_model_unlikely_threshold:
      newTurnDetector?.model?.unlikely_threshold ?? null,
    turn_detector_model_model_path: newTurnDetector?.model?.model_path ?? null,
    turn_detector_realtime_vad_type:
      newTurnDetector?.realtime_vad?.vad_type ?? REALTIME_VAD_TYPE.SEMANTIC_VAD,
    turn_detector_realtime_vad_eagerness:
      newTurnDetector?.realtime_vad?.eagerness ?? REALTIME_VAD_EAGERNESS.AUTO,
    turn_detector_realtime_vad_threshold:
      newTurnDetector?.realtime_vad?.threshold ?? null,
    turn_detector_realtime_vad_prefix_padding_ms:
      newTurnDetector?.realtime_vad?.prefix_padding_ms ?? null,
    turn_detector_realtime_vad_silence_duration_ms:
      newTurnDetector?.realtime_vad?.silence_duration_ms ?? null,
  };
};

export const formatFormToAgent = (form: AgentForm): AgentUpdateRequest => {
  const {
    keywords,
    who_speaks_first,
    activation_phrases,

    check_violations,
    check_agent_violations,

    use_agent_steps,
    max_llm_timeout,
    empty_room_timeout,
    domains_whitelist,
    request_cleaner_code,
    is_record_audio_allowed,

    automute_timeout_sec,
    autoend_after_mute_timeout_sec,
    min_endpointing_delay,

    interrupt_min_words,
    interrupt_speech_duration,

    max_endpointing_delay,

    room_life_time_limit,
    domains_white_list,
    stt_primary_model_settings,
    stt_backup_model_settings,
    tts_primary_model_settings,
    tts_backup_model_settings,
    temperature,
    link_mic_audio,
    preemptive_synthesis,
    clean_user_input_code_box,
    code_box_debug_on,
    tts_fallback_settings,
    tts_remove_exact_matches,
    tts_remove_markers,
    stt_fallback_settings,
    rag_collection_id,
    rag_similarity_threshold,
    rag_max_response_chunks,
    use_perplexity,
    room_lifetime_sec_with_no_users,
    skip_empty_user_messages,
    language_switch_by_request,
    supported_languages,
    on_agent_init_n8n_webhook,
    overall_retry_interval,
    max_overall_attempts,
    overall_stt_segment_deadline,
    internal_processing_allowance,
    use_parallel_stt,
    is_rag_enabled,
    custom_tool_configurations,
    parallel_stt_debug_mode,
    end_of_conversation_event_handler_endpoint,
    ppx_model,
    ppx_temperature,
    ppx_timeout_sec,
    ppx_search_recency_filter,
    ppx_search_context_size,
    ppx_enhanced_query,
    ppx_search_domain_filter,
    ppx_max_tokens,
    n8n_whatsup_url,
    vault_enabled,
    log_level,
    simulate_heygen_delay,
    on_phone_call_started_webhook,
    on_phone_call_ended_webhook,
    conversation_metrics_prompt,
    pii_masking_prompt,
    agent_preset_greetings,
    llm_widget_transcribe,
    llm_widget_transcribe_prompt,
    use_datetime_tool,
    use_terminate_call_tool,
    trigger_next_agent_response,
    background_audio_enabled,
    background_audio_ambient_sound,
    background_audio_ambient_volume,
    background_audio_ambient_custom_url,
    background_audio_thinking_sound,
    background_audio_thinking_volume,
    background_audio_thinking_custom_url,
    silence_triggers,

    turn_detector_mode,
    turn_detector_endpointing_mode,
    turn_detector_endpointing_min_delay,
    turn_detector_endpointing_max_delay,
    turn_detector_interruption_mode,
    turn_detector_interruption_false_interruption_timeout,
    turn_detector_interruption_resume_false_interruption,
    turn_detector_interruption_discard_audio_if_uninterruptible,
    turn_detector_vad_activation_threshold,
    turn_detector_vad_min_speech_duration,
    turn_detector_vad_min_silence_duration,
    turn_detector_vad_prefix_padding_duration,
    turn_detector_vad_max_buffered_speech,
    turn_detector_vad_sample_rate,
    turn_detector_vad_force_cpu,
    turn_detector_model_unlikely_threshold,
    turn_detector_model_model_path,
    turn_detector_realtime_vad_type,
    turn_detector_realtime_vad_eagerness,
    turn_detector_realtime_vad_threshold,
    turn_detector_realtime_vad_prefix_padding_ms,
    turn_detector_realtime_vad_silence_duration_ms,

    voicemail_detection_enabled,
    voicemail_detection_max_listen_seconds,
    voicemail_detection_max_words,
    voicemail_detection_patterns,

    ...rest
  } = form;

  return {
    ...rest,
    temperature: temperature ?? 0.3,
    stt_primary_model_settings,
    stt_backup_model_settings,
    tts_primary_model_settings,
    tts_backup_model_settings,
    end_of_conversation_event_handler_endpoint:
      end_of_conversation_event_handler_endpoint?.trim(),
    pipeline_settings: {
      use_agent_steps,
      max_llm_timeout: max_llm_timeout ?? 3,
      domains_whitelist,
      empty_room_timeout,
      request_cleaner_code,
      is_record_audio_allowed,
      supported_languages: supported_languages || [],
      rag_collection_id,
      rag_similarity_threshold,
      rag_max_response_chunks,
      use_perplexity,
      interrupt_min_words,
      automute_timeout_sec,
      autoend_after_mute_timeout_sec,
      min_endpointing_delay,
      interrupt_speech_duration,
      room_lifetime_sec_with_no_users,
      skip_empty_user_messages,
      language_switch_by_request,
      turn_detector: buildTurnDetectorConfig(form),
      parallel_stt_debug_mode,
      max_endpointing_delay,
      on_agent_init_n8n_webhook: on_agent_init_n8n_webhook?.trim(),
      room_life_time_limit,
      is_rag_enabled: Boolean(is_rag_enabled) ?? false,

      check_violations: stringToArray(check_violations || ''),
      check_agent_violations: stringToArray(check_agent_violations || ''),
      domains_white_list: stringToArrayQuote(domains_white_list || ''),
      agent_preset_greetings: parseGreetingsObjectString(
        agent_preset_greetings || ''
      ),
      link_mic_audio: link_mic_audio ?? false,
      preemptive_synthesis: preemptive_synthesis ?? true,
      clean_user_input_code_box,
      code_box_debug_on: code_box_debug_on ?? false,
      tts_fallback_settings: tts_fallback_settings
        ? JSON.parse(tts_fallback_settings || '')
        : null,
      tts_remove_exact_matches: tts_remove_exact_matches
        ? JSON.parse(tts_remove_exact_matches || '')
        : null,
      tts_remove_markers: tts_remove_markers
        ? JSON.parse(tts_remove_markers || '')
        : null,
      stt_fallback_settings: stt_fallback_settings
        ? JSON.parse(stt_fallback_settings || '')
        : null,
      custom_tool_configurations: custom_tool_configurations
        ? JSON.parse(custom_tool_configurations)
        : [],
      overall_retry_interval: Number(overall_retry_interval),
      max_overall_attempts: Number(max_overall_attempts),
      overall_stt_segment_deadline: Number(overall_stt_segment_deadline),
      internal_processing_allowance: Number(internal_processing_allowance),
      use_parallel_stt,
      ppx_model: ppx_model || 'sonar-pro',
      ppx_temperature: Number(ppx_temperature) || 0.2,
      ppx_timeout_sec: Number(ppx_timeout_sec) || 10,
      ppx_search_recency_filter: ppx_search_recency_filter || null,
      ppx_search_context_size: ppx_search_context_size || 'medium',
      ppx_enhanced_query: ppx_enhanced_query || null,
      ppx_search_domain_filter: !ppx_search_domain_filter?.length
        ? null
        : ppx_search_domain_filter,
      ppx_max_tokens: ppx_max_tokens || 200,
      n8n_whatsup_url: n8n_whatsup_url || null,
      vault_enabled: vault_enabled || false,
      log_level: log_level || 'DEBUG',
      simulate_heygen_delay: simulate_heygen_delay || false,
      on_phone_call_started_webhook:
        on_phone_call_started_webhook?.trim() || null,
      on_phone_call_ended_webhook: on_phone_call_ended_webhook?.trim() || null,
      conversation_metrics_prompt: conversation_metrics_prompt?.trim() || null,
      pii_masking_prompt: pii_masking_prompt?.trim() || null,
      llm_widget_transcribe: Boolean(llm_widget_transcribe),
      llm_widget_transcribe_prompt:
        llm_widget_transcribe_prompt?.trim() || null,
      use_datetime_tool: use_datetime_tool ?? true,
      use_terminate_call_tool: use_terminate_call_tool ?? false,
      trigger_next_agent_response: trigger_next_agent_response ?? false,
      background_audio: {
        enabled: background_audio_enabled ?? false,
        ambient_sound: toApiBackgroundSound(
          background_audio_ambient_sound,
          background_audio_ambient_custom_url
        ),
        ambient_volume: Number(background_audio_ambient_volume) ?? 0.3,
        thinking_sound: toApiBackgroundSound(
          background_audio_thinking_sound,
          background_audio_thinking_custom_url
        ),
        thinking_volume: Number(background_audio_thinking_volume) ?? 0.5,
      },
      silence_triggers: silence_triggers ?? [],
      voicemail_detection: {
        enabled: voicemail_detection_enabled ?? false,
        max_listen_seconds: Number(voicemail_detection_max_listen_seconds) || 20,
        max_words: Number(voicemail_detection_max_words) || 30,
        ...(sanitizePatternsForApi(voicemail_detection_patterns)
          ? { patterns: sanitizePatternsForApi(voicemail_detection_patterns) }
          : {}),
      },
    },
    settings: {
      who_speaks_first,
      keywords: convertStringToKeywords(keywords || ''),
      activation_phrases: stringToArray(activation_phrases || ''),
    },
  };
};
